// Consent gate for everything that is not strictly technical storage.
//
// The rule this module exists to enforce (Garante's cookie guidelines, 10 June
// 2021): nothing beyond technical storage may touch the visitor's device before
// they have expressed a choice. GA4 is not technical storage — Google uses the
// data for its own purposes as well — so it waits behind this gate.
//
// The gate is ours end to end: the banner, the stored answer and the register
// that proves it. It replaced iubenda, whose automatic blocking only rewrites
// <script> tags found in the served HTML and so never saw gtag, which
// utils/analytics.ts injects at runtime — the subscription below was already
// doing the actual work. Every tracker subscribes through onMeasurementConsent
// rather than reading storage directly.

import { recordConsent, flushPendingConsent } from '../services/consent'

/**
 * The optional purposes the banner asks about. Strictly technical storage — the
 * session token, the language, the theme, the service worker cache — is
 * deliberately absent: it is not consented to, so it is not offered as a
 * choice. Keep in step with ConsentRecord::PURPOSES on the backend.
 */
export const CONSENT_PURPOSES = ['measurement'] as const

export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number]
export type ConsentPurposes = Record<ConsentPurpose, boolean>
export type ConsentDecision = 'accept_all' | 'reject_all' | 'custom' | 'withdraw'

/**
 * Bump this whenever the policies change in a way that makes an earlier answer
 * no longer an answer to the same question — a new purpose, a new recipient of
 * the data. Everyone is then asked again, because consent is to a specific
 * processing and not to the idea of one.
 */
export const CONSENT_POLICY_VERSION = '2026-08-28'

const STORAGE_KEY = 'divepeaks-consent'
const SUBJECT_KEY = 'divepeaks-consent-subject'

/**
 * How long an answer stands before the banner comes back. The Garante's
 * guidelines forbid re-proposing the banner to someone who has already chosen
 * unless conditions change or at least six months have passed — so this is a
 * floor on nagging, not a target.
 */
const REASK_AFTER_MS = 183 * 24 * 60 * 60 * 1000

interface StoredConsent {
  version: string
  decision: ConsentDecision
  purposes: ConsentPurposes
  at: string
}

export interface ConsentState {
  /** Whether a current answer exists. False means the banner must be shown. */
  answered: boolean
  purposes: ConsentPurposes
  /** Set when the visitor reopens their choices from the footer. */
  preferencesOpen: boolean
}

type MeasurementListener = (granted: boolean) => void
type StateListener = () => void

const DENIED: ConsentPurposes = { measurement: false }

const measurementListeners = new Set<MeasurementListener>()
const stateListeners = new Set<StateListener>()

// Fail closed. Until the visitor says otherwise — including when storage is
// unreadable — every optional purpose is denied, because "we could not ask" is
// not consent.
let snapshot: ConsentState = { answered: false, purposes: DENIED, preferencesOpen: false }
let installed = false

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // Private mode, or storage disabled. Treated as no answer on record.
    return null
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // The choice still holds for this page load; it just will not survive it.
  }
}

/**
 * A random id for this browser, so the rows of one visitor read as a history.
 * Not derived from anything about the person: clearing site data produces a new
 * one, which is correct — the consent it identified is gone from the device too.
 */
function subjectId(): string {
  const existing = readStorage(SUBJECT_KEY)
  if (existing) return existing

  const generated =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `sub-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

  writeStorage(SUBJECT_KEY, generated)
  return generated
}

function normalisePurposes(value: unknown): ConsentPurposes {
  const source = (value ?? {}) as Record<string, unknown>
  // Built from the known list rather than from what was stored, so a purpose
  // added later defaults to refused instead of inheriting an older `true`.
  return CONSENT_PURPOSES.reduce((acc, purpose) => {
    acc[purpose] = source[purpose] === true
    return acc
  }, {} as ConsentPurposes)
}

/** The stored answer, if there is one that still stands. */
function readStoredConsent(): StoredConsent | null {
  const raw = readStorage(STORAGE_KEY)
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsent>
    if (parsed.version !== CONSENT_POLICY_VERSION) return null

    const at = Date.parse(parsed.at ?? '')
    if (!Number.isFinite(at) || Date.now() - at > REASK_AFTER_MS) return null

    return {
      version: parsed.version,
      decision: parsed.decision ?? 'custom',
      purposes: normalisePurposes(parsed.purposes),
      at: parsed.at as string,
    }
  } catch {
    return null
  }
}

function publish(next: ConsentState) {
  snapshot = next
  stateListeners.forEach((listener) => listener())

  // Announced on every publish, not only on a change: a refusal that repeats an
  // existing `false` is what lets subscribers clear cookies inherited from an
  // earlier visit under a previous configuration. The listeners are idempotent.
  measurementListeners.forEach((listener) => listener(next.purposes.measurement))
}

/**
 * Persists an answer and tells everyone. The register entry is best-effort and
 * deliberately does not gate the local decision: a visitor who refuses is
 * refused immediately, whether or not our own server heard about it.
 */
function decide(decision: ConsentDecision, purposes: ConsentPurposes) {
  const stored: StoredConsent = {
    version: CONSENT_POLICY_VERSION,
    decision,
    purposes,
    at: new Date().toISOString(),
  }

  writeStorage(STORAGE_KEY, JSON.stringify(stored))
  publish({ answered: true, purposes, preferencesOpen: false })

  recordConsent({
    subject_id: subjectId(),
    decision,
    purposes,
    policy_version: CONSENT_POLICY_VERSION,
    locale: document.documentElement.lang || 'en',
  })
}

export function acceptAll() {
  decide('accept_all', CONSENT_PURPOSES.reduce((acc, purpose) => {
    acc[purpose] = true
    return acc
  }, {} as ConsentPurposes))
}

export function rejectAll() {
  decide('reject_all', { ...DENIED })
}

/**
 * Saves a per-purpose choice. Recorded as a withdrawal when it takes something
 * away that had been granted, because "I changed my mind" is the event a
 * register has to be able to show.
 */
export function saveCustomConsent(purposes: ConsentPurposes) {
  const revokes = CONSENT_PURPOSES.some(
    (purpose) => snapshot.purposes[purpose] && !purposes[purpose],
  )
  decide(revokes ? 'withdraw' : 'custom', { ...purposes })
}

/** Reopens the panel from the footer, so a choice is never a one-way door. */
export function openConsentPreferences() {
  publish({ ...snapshot, preferencesOpen: true })
}

export function closeConsentPreferences() {
  publish({ ...snapshot, preferencesOpen: false })
}

/**
 * Subscribes to measurement consent. The listener fires immediately with the
 * current answer, so it does not matter whether a tracker registers before or
 * after the banner has resolved.
 */
export function onMeasurementConsent(listener: MeasurementListener): () => void {
  measurementListeners.add(listener)
  listener(snapshot.purposes.measurement)
  return () => measurementListeners.delete(listener)
}

export function hasMeasurementConsent(): boolean {
  return snapshot.purposes.measurement
}

/** The two halves React needs for useSyncExternalStore. */
export function subscribeToConsent(listener: StateListener): () => void {
  stateListeners.add(listener)
  return () => stateListeners.delete(listener)
}

export function getConsentSnapshot(): ConsentState {
  return snapshot
}

/**
 * Hydrates the gate from storage. Loads no tracker by itself — it only decides
 * whether the banner has to be shown and what the trackers are allowed to do.
 */
export function initConsent() {
  if (installed) return
  installed = true

  const stored = readStoredConsent()
  publish({
    answered: stored !== null,
    purposes: stored ? stored.purposes : DENIED,
    preferencesOpen: false,
  })

  // An answer given while the server was unreachable is still an answer; this
  // is the only chance to get it into the register.
  flushPendingConsent()
}

/** Test seam: lets a spec drive the gate without going through the banner. */
export function __setMeasurementConsentForTest(next: boolean) {
  publish({ answered: true, purposes: { measurement: next }, preferencesOpen: false })
}

/** Test seam: resets module state between specs. */
export function __resetConsentForTest() {
  measurementListeners.clear()
  stateListeners.clear()
  snapshot = { answered: false, purposes: DENIED, preferencesOpen: false }
  installed = false
}
